import { useEffect, useState } from 'react';
import { Modal } from '@components/common';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Experience } from '@components/templates/job/after-apply/Experience';
import { Languages } from '@components/templates/job/after-apply/Languages';
import {
  ApiApplyWizardStepList,
  fetchAbort,
  fetcher,
  isApplyWizardStepListOk,
} from '@utils/data-fetching';
import { ApplyWizardStepList } from 'src/types/apply-wizard';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { Study } from '@components/templates/job/after-apply/Study';
import { RequirementQuestions } from '@components/templates/job/after-apply/RequirementQuestions';
import UnifiedJobInterview from './job-interview/UnifiedJobInterview';

interface MissingStepsModalProps {
  jobId: string;
  jobSlug: string;
  successCallback?: () => void;
  noMissingStepsCallback?: () => void;
}

/**
 * @description Shows a modal for each missing data step, after applying to a job
 * Displays modals for skills, languages and experience that are missing from the user's profile
 *
 * - **jobId** - The job ID used to fetch missing steps (as a string)
 * - **successCallback** - Optional callback function that is called when all steps are completed or when flow is closed early
 * - **noMissingStepsCallback** - Optional callback function that is called when there are no missing steps
 */
export const MissingStepsModal = NiceModal.create(
  (props: MissingStepsModalProps) => {
    const {
      jobId,
      jobSlug,
      successCallback,
      noMissingStepsCallback,
    } = props;
    const modal = useModal();
    const { t } = useTranslation('common');

    // Get locale from router
    const { locale } = useExtendedRouter();

    const [missingSteps, setMissingSteps] = useState<string[]>([]);
    const [stepToShow, setStepToShow] = useState<string>('');
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    useEffect(() => {
      const { controller, timeoutId } = fetchAbort();

      // get missing data
      void fetcher<ApiApplyWizardStepList>(
        `/v1/apply-wizard/${jobId}/step-list`,
        locale,
        {
          externalAbort: { extController: controller, extTimeout: timeoutId },
        },
      ).then((response) => {
        if (isApplyWizardStepListOk(response)) {
          // Keep the steps that are handled in the rendering
          const filteredSteps = response.filter((step) => [
            ApplyWizardStepList.language,
            ApplyWizardStepList.study,
            ApplyWizardStepList.experience,
            ApplyWizardStepList.miniInterview,
            ApplyWizardStepList.videoInterview,
            ApplyWizardStepList.requirements,
          ].includes(step as ApplyWizardStepList));

          // Map miniInterview and videoInterview to 'interview'
          const mappedSteps = filteredSteps.map((step) => {
            if (step === 'miniInterview' || step === 'videoInterview') {
              return ApplyWizardStepList.interview;
            }
            return step;
          });

          // Deduplicate
          const uniqueSteps = [...new Set(mappedSteps)];

          // Reorder steps to put requirements first
          const reorderedSteps = uniqueSteps.sort((a, b) => {
            if (a === ApplyWizardStepList.requirements as string) return -1;
            if (b === ApplyWizardStepList.requirements as string) return 1;
            return 0;
          });

          setMissingSteps(reorderedSteps);

          if (reorderedSteps.length > 0) {
            setModalOpen(true);
          } else {
            // If no missing steps, call noMissingStepsCallback if provided
            noMissingStepsCallback?.();
          }
        }
      });

      return () => {
        controller.abort();
      };
    }, [jobId, locale, noMissingStepsCallback]);

    // resets data when we have missing steps information available
    useEffect(() => {
      if (missingSteps?.length) {
        setCurrentStep(1);
        setStepToShow(missingSteps[0]);
      }
    }, [missingSteps, modal]);

    const onCloseModal = () => {
      // If we're on the last step, close the modal
      if (currentStep >= missingSteps.length) {
        setModalOpen(false);
        successCallback?.();
      } else {
        // Otherwise, advance to the next step
        const oldCurrentStep = currentStep;
        setCurrentStep((prevState) => prevState + 1);
        setStepToShow(missingSteps[oldCurrentStep]);
      }
    };

    const noExpAction = () => {
      if (missingSteps.includes(ApplyWizardStepList.study)) {
        const oldCurrentStep = currentStep;
        setCurrentStep((prevState) => prevState + 1);
        setStepToShow(missingSteps[oldCurrentStep]);
      }
    };

    const onSaveSuccess = () => {
      const oldCurrentStep = currentStep;

      // Check if we've completed all steps
      if (oldCurrentStep + 1 > missingSteps.length) {
        // Reset missing steps and close modal
        setMissingSteps([]);
        setModalOpen(false);
        // Call success callback if provided
        successCallback?.();
      } else {
        // Move to next step
        setCurrentStep((prevState) => prevState + 1);
        setStepToShow(missingSteps[oldCurrentStep]);
      }
    };

    return (
      <>
        {missingSteps?.length > 0 && currentStep <= missingSteps?.length && (
          <Modal
            size="lg"
            isOpen={modalOpen}
            onClose={onCloseModal}
            backdropClickDisabled
            header={
              {
                // title of the modal
                [ApplyWizardStepList.experience]: t('apply.wizard.experience.title'),
                [ApplyWizardStepList.language]: t('apply.wizard.languages.title'),
                [ApplyWizardStepList.study]: t('apply.wizard.study.title'),
                [ApplyWizardStepList.interview]: t('apply.wizard.interview.title'),
                [ApplyWizardStepList.requirements]: t('apply.wizard.requirements.title'),
              }[stepToShow]
            }
            headerClasses="text-xl"
            body={(
              <div className="p-6">
                {
                  {
                    // content of the modal based on step
                    [ApplyWizardStepList.experience]: (
                      <Experience
                        noExpAction={noExpAction}
                        onSaveSuccess={onSaveSuccess}
                        locale={locale}
                      />
                    ),
                    [ApplyWizardStepList.study]: (
                      <Study onSaveSuccess={onSaveSuccess} locale={locale} />
                    ),
                    [ApplyWizardStepList.language]: (
                      <Languages
                        onSaveSuccess={onSaveSuccess}
                        locale={locale}
                      />
                    ),
                    [ApplyWizardStepList.interview]: (
                      <UnifiedJobInterview
                        jobSlug={jobSlug}
                        locale={locale}
                      />
                    ),
                    [ApplyWizardStepList.requirements]: (
                      <RequirementQuestions
                        jobSlug={jobSlug}
                        locale={locale}
                        onSaveSuccess={onSaveSuccess}
                      />
                    ),
                  }[stepToShow]
                }
              </div>
            )}
          />
        )}
      </>
    );
  },
);

/**
 * @description Opens the missing steps modal anywhere in the app
 * This modal guides users to complete their profile by filling in missing information
 * after they have applied to a job.
 *
 * - **jobId** - The job ID used to fetch missing steps (as a string)
 * - **successCallback** - Optional callback function that is called when all steps are completed
 * - **noMissingStepsCallback** - Optional callback function that is called when there are no missing steps
 *
 * @example
 * // import the function
 * import { showMissingStepsModal } from '@components/templates/job/after-apply/MissingStepsModal';
 * // usage
 * <Button onClick={() => showMissingStepsModal({ jobId: String(job.id), successCallback })}>Show missing steps modal</Button>
 */
export const showMissingStepsModal = (props: MissingStepsModalProps) => {
  void NiceModal.show(MissingStepsModal, props);
};
